/**
 * Users (real `/v2/user/` backend). Used to populate the UW / Analyst
 * filter dropdowns (assignee_id / approver_id). The endpoint paginates
 * with `?limit=&offset=` (limit capped at 50 server-side) and applies no
 * role/active filtering or ordering, so we drop inactive users and sort
 * alphabetically here until the backend grows a proper filter.
 */
import { getJsonReal } from "../client";
import type { UserDisplay } from "../../types/submission";

const PAGE_SIZE = 50;
/** Safety stop for the paged fetch (500 users). */
const MAX_PAGES = 10;

interface UserWire {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    is_active?: boolean;
    roles?: { id: string; is_active: boolean }[];
}

/** Full name, falling back to email, then a stable placeholder. */
export function userLabel(u: UserDisplay): string {
    return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || `User #${u.id}`;
}

/** `search` is sent for forward-compatibility — the endpoint ignores it
 *  today, so callers must still narrow client-side until the backend
 *  implements it (asked as an OR-1173 follow-up). */
export async function listUsers(params: { search?: string } = {}): Promise<UserDisplay[]> {
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (params.search) qs.set("search", params.search);
    return toActiveSorted(await getJsonReal<UserWire[]>(`/v2/user/?${qs}`));
}

/** Every active user, walking the pages. Used where the whole roster must
 *  be present (submission header Underwriter / Approval UW), since a single
 *  page can leave out a user that is already assigned. */
export async function listAllUsers(): Promise<UserDisplay[]> {
    const rows: UserWire[] = [];
    for (let page = 0; page < MAX_PAGES; page++) {
        const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
        const batch = await getJsonReal<UserWire[]>(`/v2/user/?${qs}`);
        rows.push(...batch);
        if (batch.length < PAGE_SIZE) break;
    }
    return toActiveSorted(rows);
}

function toActiveSorted(rows: UserWire[]): UserDisplay[] {
    return rows
        .filter((u) => u.is_active !== false)
        .map((u) => ({ id: u.id, first_name: u.first_name, last_name: u.last_name, email: u.email }))
        .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
}
